import { Link, useLocation } from 'react-router-dom'
import { useMemo, useState, type ReactNode } from 'react'
import { I18nProvider, LangToggle } from './i18n'
import type { Dict } from './i18n-ctx'
import ChatWidget, { CHAT_DICT, type ChatPersona } from './ChatWidget'
import { calcCommission, COMMISSION, getProduct } from '../../lib/products'

export default function DemoLayout({
  dict,
  children,
  accent = 'from-brand-500 to-brand-700',
  chatPersona,
}: {
  dict: Dict
  children: ReactNode
  /** Tailwind gradient classes for the accent strip below the banner. */
  accent?: string
  /** Business persona so the chat widget answers in-character + simulates booking. */
  chatPersona?: ChatPersona
}) {
  const location = useLocation()
  const [pitchOpen, setPitchOpen] = useState(false)
  const product = useMemo(() => {
    const slug = location.pathname.match(/\/demo\/([^/?#]+)/)?.[1]
    return slug ? getProduct(decodeURIComponent(slug)) : null
  }, [location.pathname])
  // Merge caller's dictionary with chat-widget strings so useT() resolves
  // both "book_now" (caller) and "chat_title" (widget) inside the same Provider.
  const mergedDict = { ...CHAT_DICT, ...dict }
  return (
    <I18nProvider dict={mergedDict}>
      {/* Demo banner — always visible so viewers know this is a sample */}
      <div className="sticky top-0 z-40 bg-amber-500/10 backdrop-blur border-b border-amber-500/20 text-amber-300 text-xs font-medium">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div>
            🎬 <span className="font-semibold">This is a demo.</span>{' '}
            <span className="hidden sm:inline">Fake data. No real bookings will be made.</span>{' '}
            Built by <Link to="/" className="underline text-amber-200 hover:text-white">Qordo Catalog</Link>.
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/story"
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-400/15"
            >
              ▶ How partners earn
            </Link>
            {product && (
              <button
                type="button"
                onClick={() => setPitchOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200 hover:bg-emerald-400/15"
              >
                <MoneyIcon />
                Pitch mode
              </button>
            )}
            <LangToggle />
          </div>
        </div>
      </div>
      <div className={`h-1 bg-gradient-to-r ${accent}`} aria-hidden />
      {children}
      {product && (
        <>
          <button
            type="button"
            onClick={() => setPitchOpen(true)}
            className="fixed bottom-24 right-5 z-40 hidden sm:inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-ink-900/95 px-4 py-2 text-sm font-semibold text-emerald-200 shadow-xl shadow-black/30 backdrop-blur hover:border-emerald-300"
          >
            <MoneyIcon />
            Sell this demo
          </button>
          <PitchPanel product={product} open={pitchOpen} onClose={() => setPitchOpen(false)} />
        </>
      )}
      {!pitchOpen && <ChatWidget persona={chatPersona} />}
    </I18nProvider>
  )
}

type ProductForPitch = NonNullable<ReturnType<typeof getProduct>>
type PlanKey = 'starter' | 'pro' | 'business'

function PitchPanel({
  product,
  open,
  onClose,
}: {
  product: ProductForPitch
  open: boolean
  onClose: () => void
}) {
  const [plan, setPlan] = useState<PlanKey>('pro')
  const comm = calcCommission(product.plans[plan].price)
  const pitchLink = `/catalog/${product.slug}`

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm">
      <button type="button" aria-label="Close pitch mode" className="absolute inset-0 cursor-default" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-[440px] flex-col overflow-y-auto border-l border-ink-800 bg-ink-950 shadow-2xl">
        <header className="sticky top-0 z-10 border-b border-ink-800 bg-ink-950/95 p-4 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-300">Partner pitch mode</div>
              <h2 className="mt-1 text-xl font-bold text-white">{product.name}</h2>
              <p className="mt-1 text-sm text-ink-500">Use this while the owner is watching the demo on your phone.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close pitch mode"
              className="rounded-lg p-2 text-ink-500 hover:bg-ink-900 hover:text-white"
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="space-y-4 p-4">
          <section className="rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-4">
            <div className="text-sm font-semibold text-emerald-200">Lead with this line</div>
            <p className="mt-2 text-base font-semibold leading-snug text-white">
              "This is not a website. It is a working mini-system that takes the booking, order, or quote while you are busy."
            </p>
          </section>

          <section className="rounded-2xl border border-ink-800 bg-ink-900/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white">Your commission if they choose</div>
                <div className="mt-1 text-xs text-ink-500">
                  EUR {COMMISSION.setupFee} setup + {COMMISSION.recurringPercent}% for {COMMISSION.recurringMonths} months
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-emerald-300">EUR {comm.total6m}</div>
                <div className="text-xs text-ink-500">first 6 months</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {(['starter', 'pro', 'business'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlan(p)}
                  className={`rounded-xl border px-2 py-2 text-center text-xs font-semibold capitalize ${
                    plan === p
                      ? 'border-brand-500 bg-brand-600 text-white'
                      : 'border-ink-800 bg-ink-950 text-ink-100 hover:border-ink-500'
                  }`}
                >
                  {p}
                  <span className="block text-[11px] font-medium opacity-75">EUR {product.plans[p].price}/mo</span>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-ink-800 bg-ink-900/70 p-4">
            <div className="text-sm font-semibold text-white">What to show in 60 seconds</div>
            <ol className="mt-3 space-y-3 text-sm text-ink-100">
              <PitchStep n="1" text="Ask which customer action wastes the most time: calls, DMs, no-shows, orders, or quotes." />
              <PitchStep n="2" text="Let them tap the demo themselves. Do not explain too much. Make them feel the workflow." />
              <PitchStep n="3" text="Open the chat bubble and ask for a price, booking, order, or quote. Show the confirmation." />
              <PitchStep n="4" text="Say the price, then stop talking. If they ask details, they are interested." />
            </ol>
          </section>

          <PitchCopy title="Business pain to repeat back" text={product.whyNeed} />
          <PitchCopy title="Money argument" text={product.moneyImpact} />

          <section className="rounded-2xl border border-ink-800 bg-ink-900/70 p-4">
            <div className="text-sm font-semibold text-white">If they object</div>
            <div className="mt-3 space-y-3 text-sm">
              <Objection q="I already use Instagram / WhatsApp." a="Good. This does not replace that. It gives those messages one clean place to become bookings, orders, or quotes." />
              <Objection q="I do not need another system." a="Then test one workflow first. If it does not save time or bring leads, do not continue." />
              <Objection q="It is too expensive." a={`The popular plan is EUR ${product.plans.pro.price}/month. One recovered sale or one saved no-show usually pays for it.`} />
            </div>
          </section>

          <section className="rounded-2xl border border-ink-800 bg-ink-900/70 p-4">
            <div className="text-sm font-semibold text-white">Close the next step</div>
            <div className="mt-3 grid gap-2">
              <Link to={pitchLink} className="btn-primary w-full" onClick={onClose}>
                Open product + submit lead
              </Link>
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => {
                  void navigator.clipboard?.writeText(`${window.location.origin}${pitchLink}`)
                }}
              >
                Copy product link
              </button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-ink-500">
              Do not create fake leads while testing production. Real partner signups affect HQ metrics.
            </p>
          </section>
        </div>
      </aside>
    </div>
  )
}

function PitchCopy({ title, text }: { title: string; text: string }) {
  return (
    <section className="rounded-2xl border border-ink-800 bg-ink-900/70 p-4">
      <div className="text-sm font-semibold text-white">{title}</div>
      <p className="mt-2 text-sm leading-relaxed text-ink-100">{text}</p>
    </section>
  )
}

function PitchStep({ n, text }: { n: string; text: string }) {
  return (
    <li className="flex gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-600 text-xs font-bold text-white">{n}</span>
      <span>{text}</span>
    </li>
  )
}

function Objection({ q, a }: { q: string; a: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-950 p-3">
      <div className="font-semibold text-amber-200">"{q}"</div>
      <div className="mt-1 text-ink-100">{a}</div>
    </div>
  )
}

function MoneyIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M17 7.5c-.8-1-2.3-1.8-4.2-1.8-2.5 0-4.3 1.2-4.3 3 0 4.4 8.9 1.7 8.9 6.4 0 1.9-1.9 3.2-4.7 3.2-2.1 0-3.9-.8-5-2.1" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  )
}
